const KNOWN_SETTING_LABEL_KEYS = {
  franchise_id: "dynamicLabels.franchiseId",
  language_code: "dynamicLabels.languageCode",
  provider_external_id: "dynamicLabels.providerExternalId",
  country_code: "dynamicLabels.countryCode",
  phone_number: "dynamicLabels.phoneNumber",
  phone_number_id: "dynamicLabels.phoneNumberId",
  account_id: "dynamicLabels.accountId",
  api_key: "dynamicLabels.apiKey",
  access_token: "dynamicLabels.accessToken",
  secret_key: "dynamicLabels.secretKey",
  webhook_url: "dynamicLabels.webhookUrl",
  website_url: "dynamicLabels.websiteUrl",
};

const KNOWN_SCHEMA_TITLE_KEYS = {
  "finitless chat provider settings":
    "dynamicSchemaTitles.finitlessChatProviderSettings",
};

const KNOWN_SCHEMA_DESCRIPTION_KEYS = {
  "settings for finitless platform integration":
    "dynamicSchemaDescriptions.finitlessPlatformIntegration",
};

const SPANISH_WORD_MAP = {
  language: "idioma",
  code: "codigo",
  franchise: "franquicia",
  provider: "proveedor",
  settings: "configuracion",
  setting: "configuracion",
  integration: "integracion",
  platform: "plataforma",
  chat: "chat",
  phone: "telefono",
  number: "numero",
  account: "cuenta",
  api: "api",
  key: "clave",
  access: "acceso",
  token: "token",
  secret: "secreto",
  webhook: "webhook",
  website: "sitio web",
  url: "url",
  id: "id",
  type: "tipo",
  for: "para",
};

const toWords = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const titleCase = (value) =>
  value
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const isSpanishLocale = (locale) => String(locale || "").startsWith("es");

const translateWordByWord = (value, locale) => {
  const normalized = toWords(value);
  if (!normalized) {
    return "";
  }

  if (!isSpanishLocale(locale)) {
    return titleCase(normalized.toLowerCase());
  }

  const translated = normalized
    .toLowerCase()
    .split(" ")
    .map((word) => SPANISH_WORD_MAP[word] || word)
    .join(" ");

  return titleCase(translated);
};

const getProviderLabel = (provider, t, locale) => {
  switch (provider) {
    case "whatsapp":
      return t("providers.whatsapp");
    case "phone":
      return t("providers.phone");
    case "web":
      return t("providers.web");
    case "finitless":
      return "Finitless";
    default:
      return translateWordByWord(provider, locale);
  }
};

const getLocalizedKnownPhrase = (value, map, t) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  const key = map[normalized];
  return key ? t(key) : null;
};

export const resolveAgentSettingLabel = ({ key, title, t, locale }) => {
  if (key && KNOWN_SETTING_LABEL_KEYS[key]) {
    return t(KNOWN_SETTING_LABEL_KEYS[key]);
  }

  if (title) {
    const knownTitle = getLocalizedKnownPhrase(
      title,
      KNOWN_SCHEMA_TITLE_KEYS,
      t,
    );
    if (knownTitle) {
      return knownTitle;
    }
    return translateWordByWord(title, locale);
  }

  return translateWordByWord(key, locale);
};

export const resolveSchemaTitle = ({ title, provider, type, t, locale }) => {
  const knownTitle = getLocalizedKnownPhrase(title, KNOWN_SCHEMA_TITLE_KEYS, t);
  if (knownTitle) {
    return knownTitle;
  }

  if (title) {
    return translateWordByWord(title, locale);
  }

  return t("dynamicSchema.defaultTitle", {
    provider: getProviderLabel(provider, t, locale),
    type: translateWordByWord(type, locale),
  });
};

export const resolveSchemaDescription = ({
  description,
  provider,
  type,
  t,
  locale,
}) => {
  const knownDescription = getLocalizedKnownPhrase(
    description,
    KNOWN_SCHEMA_DESCRIPTION_KEYS,
    t,
  );
  if (knownDescription) {
    return knownDescription;
  }

  if (description) {
    return translateWordByWord(description, locale);
  }

  return t("dynamicSchema.defaultDescription", {
    provider: getProviderLabel(provider, t, locale),
    type: translateWordByWord(type, locale),
  });
};

export const localizeGenericBackendText = (value, locale) => {
  return translateWordByWord(value, locale);
};
